import { useState, useRef, useCallback, useEffect } from 'react';
import toast from 'react-hot-toast';
import { generateEncryptionKey, encryptAudioBlob, exportKey, AUDIO_ENCRYPTION_CONFIG } from '../utils/crypto';
import { recordingStorage, StoredRecording } from '../services/recordingStorage';
import { v4 as uuidv4 } from 'uuid';
import { convertToWav, isAzureCompatibleFormat, getAzureOptimizedRecordingSettings } from '../utils/audioConversion';
import { analyzeAudioIntegrity, generateQualityReport, getOptimizationParameters } from '../utils/audioIntegrity';
import { chunkStorageService } from '../services/chunkStorage.service';
import { useRecordingSessionStore } from '../stores/recordingSessionStore';
import { appLifecycleService } from '../services/appLifecycle.service';

interface AudioRecorderState {
  isRecording: boolean;
  isPaused: boolean;
  recordingTime: number;
  audioBlob: Blob | null;
  audioUrl: string | null;
  isEncrypting: boolean;
  encryptedRecording: StoredRecording | null;
  error: string | null;
  audioLevel: number; // 0-100 volume level
  hasAudioInput: boolean; // Whether we're receiving audio
  // Protection state
  savedChunksCount: number;
  lastChunkSavedAt: Date | null;
  sessionId: string | null;
}

interface UseAudioRecorderReturn extends AudioRecorderState {
  startRecording: () => Promise<void>;
  stopRecording: () => void;
  pauseRecording: () => void;
  resumeRecording: () => void;
  resetRecording: () => void;
  flushChunks: () => Promise<void>; // Force flush chunks to storage
  isSupported: boolean;
  waveformData: number[]; // Array of audio levels for visualization
}

export const useAudioRecorder = (
  appointmentId: string,
  patientId: string,
  providerId: string,
  encounterId?: string,
  patientName?: string
): UseAudioRecorderReturn => {
  const [state, setState] = useState<AudioRecorderState>({
    isRecording: false,
    isPaused: false,
    recordingTime: 0,
    audioBlob: null,
    audioUrl: null,
    isEncrypting: false,
    encryptedRecording: null,
    error: null,
    audioLevel: 0,
    hasAudioInput: false,
    savedChunksCount: 0,
    lastChunkSavedAt: null,
    sessionId: null,
  });

  const [waveformData, setWaveformData] = useState<number[]>([]);

  // Recording session store for persistence
  const {
    startSession: startStoreSession,
    pauseSession: pauseStoreSession,
    resumeSession: resumeStoreSession,
    completeSession: completeStoreSession,
    updateDuration,
    addSegment,
    updateCurrentSegment,
    endCurrentSegment,
    clearActiveSession,
  } = useRecordingSessionStore();

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const startTimeRef = useRef<number>(0);
  const pausedTimeRef = useRef<number>(0);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const chunkFlushIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const sessionIdRef = useRef<string | null>(null);
  const mimeTypeRef = useRef<string>('audio/webm');

  const isSupported = !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);

  // Flush chunks to storage
  const flushChunks = useCallback(async () => {
    if (!sessionIdRef.current || !state.isRecording) return;

    try {
      const result = await chunkStorageService.flushBuffer(false);
      if (result) {
        setState(prev => ({
          ...prev,
          savedChunksCount: prev.savedChunksCount + 1,
          lastChunkSavedAt: new Date(),
        }));
        updateCurrentSegment({ chunkCount: state.savedChunksCount + 1 });
      }
    } catch (error) {
      console.error('[useAudioRecorder] Failed to flush chunks:', error);
    }
  }, [state.isRecording, state.savedChunksCount, updateCurrentSegment]);

  // Initialize app lifecycle service and handle background events
  useEffect(() => {
    appLifecycleService.initialize();

    const unsubscribe = appLifecycleService.subscribe(async (isActive, lifecycleState) => {
      console.log('[useAudioRecorder] App lifecycle change:', { isActive, lifecycleState, isRecording: state.isRecording });

      if (!isActive && state.isRecording && !state.isPaused) {
        // App going to background while recording - silently flush chunks for safety
        // Do NOT interrupt the session or show toast - let the user continue normally
        // onRehydrate in recordingSessionStore will detect actual crashes on app restart
        try {
          await chunkStorageService.flushBuffer(true);
          console.log('[useAudioRecorder] Chunks flushed on background - recording continues');
        } catch (error) {
          console.error('[useAudioRecorder] Emergency flush failed:', error);
        }
      }
    });

    return () => {
      unsubscribe();
    };
  }, [state.isRecording, state.isPaused]);

  // Periodic chunk flush interval
  useEffect(() => {
    if (state.isRecording && !state.isPaused) {
      // Check and flush every 5 seconds (actual flush happens at 30s intervals in service)
      chunkFlushIntervalRef.current = setInterval(async () => {
        if (chunkStorageService.shouldFlushBuffer()) {
          await flushChunks();
        }
      }, 5000);

      return () => {
        if (chunkFlushIntervalRef.current) {
          clearInterval(chunkFlushIntervalRef.current);
          chunkFlushIntervalRef.current = null;
        }
      };
    }
  }, [state.isRecording, state.isPaused, flushChunks]);

  const updateRecordingTime = useCallback(() => {
    if (intervalRef.current) {
      const now = Date.now();
      const elapsed = now - startTimeRef.current - pausedTimeRef.current;
      setState(prev => ({ ...prev, recordingTime: Math.floor(elapsed / 1000) }));
    }
  }, []);

  const startRecording = useCallback(async () => {
    if (!isSupported) {
      toast.error('Audio recording is not supported in this browser');
      return;
    }

    try {
      // Request microphone access with Azure-optimized settings
      const azureSettings = getAzureOptimizedRecordingSettings();
      const stream = await navigator.mediaDevices.getUserMedia(azureSettings);

      streamRef.current = stream;
      chunksRef.current = [];

      // Generate session ID for chunk storage
      const sessionId = uuidv4();
      sessionIdRef.current = sessionId;

      // Create MediaRecorder with Azure Speech compatible formats
      let mimeType;
      if (MediaRecorder.isTypeSupported('audio/wav')) {
        mimeType = 'audio/wav';
      } else if (MediaRecorder.isTypeSupported('audio/mp4')) {
        mimeType = 'audio/mp4';
      } else if (MediaRecorder.isTypeSupported('audio/webm;codecs=pcm')) {
        mimeType = 'audio/webm;codecs=pcm';
      } else if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
        mimeType = 'audio/webm;codecs=opus';
      } else {
        mimeType = 'audio/webm';
      }
      
      mimeTypeRef.current = mimeType;

      // Initialize chunk storage session for protection
      const encryptionKeyId = await chunkStorageService.startSession(sessionId, mimeType);

      // Initialize recording session store for recovery
      startStoreSession({
        sessionId,
        appointmentId: appointmentId || undefined,
        encounterId: encounterId || undefined,
        patientId,
        providerId,
        patientName,
        encryptionKeyId,
        mimeType,
      });

      // Add initial segment
      addSegment({
        id: uuidv4(),
        startTime: Date.now(),
        duration: 0,
        chunkCount: 0,
        mimeType,
      });

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType,
        audioBitsPerSecond: 128000,
      });

      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
          // Add to chunk buffer for incremental saving
          chunkStorageService.addToBuffer(event.data);
        }
      };

      // This is the ONLY onstop handler - handles all processing
      mediaRecorder.onstop = async () => {

        try {
          setState(prev => ({ ...prev, isEncrypting: true }));

          // Create blob from chunks
          let audioBlob = new Blob(chunksRef.current, { type: mimeType });
          const recordingDuration = Math.floor((Date.now() - startTimeRef.current - pausedTimeRef.current) / 1000);

          console.log('Recording stopped:', {
            blobSize: audioBlob.size,
            chunks: chunksRef.current.length,
            mimeType: mimeType,
            duration: recordingDuration
          });

          // BULLETPROOF AUDIO INTEGRITY ANALYSIS (optional, don't block on failure)
          try {
            const integrityReport = await analyzeAudioIntegrity(audioBlob, recordingDuration);
            console.log('Audio quality:', integrityReport.quality, `(${(integrityReport.maxAmplitude * 100).toFixed(1)}% peak)`);

            const optimizationParams = getOptimizationParameters(integrityReport);

            // Attach metadata for transcription service
            (audioBlob as any).__integrityReport = integrityReport;
            (audioBlob as any).__optimizationParams = optimizationParams;
          } catch (analysisError) {
            console.warn('Audio analysis failed but continuing:', analysisError);
          }

          // Convert to WAV if not Azure compatible
          if (!isAzureCompatibleFormat(audioBlob.type)) {
            try {
              toast.loading('Converting audio for better compatibility...', { duration: 2000 });
              const conversionResult = await convertToWav(audioBlob);
              audioBlob = conversionResult.convertedBlob;
              console.log('Audio converted successfully:', {
                originalFormat: conversionResult.originalFormat,
                convertedFormat: conversionResult.convertedFormat,
                originalSize: conversionResult.originalSize,
                convertedSize: conversionResult.convertedSize
              });
            } catch (conversionError) {
              console.error('Audio conversion failed:', conversionError);
              // Continue with original blob
            }
          }

          // Generate encryption key
          const encryptionKey = await generateEncryptionKey();

          // Encrypt the audio
          const encrypted = await encryptAudioBlob(audioBlob, encryptionKey);

          // Create stored recording object
          const encryptionMetadata = {
            iv: encrypted.iv,
            authTag: encrypted.authTag,
            algorithm: AUDIO_ENCRYPTION_CONFIG.algorithm,
            keyId: await exportKey(encryptionKey)
          };

          const storedRecording: StoredRecording = {
            id: uuidv4(),
            appointmentId,
            encounterId: encounterId || undefined,
            patientId,
            providerId,
            encryptedAudio: encrypted.encryptedData,
            encryptionMetadata,
            recordingMetadata: {
              duration: recordingDuration,
              size: audioBlob.size,
              mimeType: audioBlob.type,
              recordedAt: new Date(),
              deviceInfo: {
                userAgent: navigator.userAgent,
                platform: navigator.platform
              }
            },
            uploadStatus: 'pending',
            uploadAttempts: 0
          };

          // Save to IndexedDB
          await recordingStorage.saveRecording(storedRecording);

          // Create object URL for playback
          const audioUrl = URL.createObjectURL(audioBlob);

          setState(prev => ({
            ...prev,
            isRecording: false,
            isPaused: false,
            audioBlob,
            audioUrl,
            isEncrypting: false,
            encryptedRecording: storedRecording,
            error: null
          }));

          // Complete the session in store (marks as completed, NOT interrupted)
          completeStoreSession();

          // Clean up chunks after successful save
          if (sessionIdRef.current) {
            try {
              await chunkStorageService.deleteSessionChunks(sessionIdRef.current);
            } catch (cleanupError) {
              console.warn('[useAudioRecorder] Failed to cleanup chunks:', cleanupError);
            }
          }

          // Clear active session from store
          clearActiveSession();

          toast.success('Recording saved securely');
        } catch (error) {
          console.error('Failed to process recording:', error);
          setState(prev => ({
            ...prev,
            isRecording: false,
            isPaused: false,
            isEncrypting: false,
            error: 'Failed to encrypt and save recording'
          }));
          toast.error('Failed to save recording');
        }

        // Stop the stream
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop());
          streamRef.current = null;
        }

        // Clear session ref
        sessionIdRef.current = null;
      };

      // Set up audio analysis for level monitoring
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      
      // Resume audio context if suspended (required by some browsers)
      if (audioContext.state === 'suspended') {
        await audioContext.resume();
      }
      
      const analyser = audioContext.createAnalyser();
      const source = audioContext.createMediaStreamSource(stream);
      
      // Optimized settings for better real-time detection
      analyser.fftSize = 2048;  // Higher resolution
      analyser.smoothingTimeConstant = 0.3; // Less smoothing for more responsive
      analyser.minDecibels = -90;
      analyser.maxDecibels = -10;
      
      source.connect(analyser);
      
      audioContextRef.current = audioContext;
      analyserRef.current = analyser;

      // Start monitoring audio levels
      const monitorAudioLevel = () => {
        if (!analyserRef.current) return;
        
        const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
        analyserRef.current.getByteFrequencyData(dataArray);
        
        // Also get time domain data for better silence detection
        const timeDomainData = new Uint8Array(analyserRef.current.frequencyBinCount);
        analyserRef.current.getByteTimeDomainData(timeDomainData);
        
        // Calculate RMS from time domain data (more accurate for speech)
        let sum = 0;
        let max = 0;
        for (let i = 0; i < timeDomainData.length; i++) {
          const normalized = (timeDomainData[i] - 128) / 128;
          const abs = Math.abs(normalized);
          sum += normalized * normalized;
          max = Math.max(max, abs);
        }
        const rms = Math.sqrt(sum / timeDomainData.length);
        
        // Calculate average volume level from frequency data
        const average = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
        const freqLevel = (average / 255) * 100; // Use 255 as max for frequency data
        
        // Scale RMS and max levels
        const rmsLevel = Math.min(100, rms * 500); // Increased sensitivity
        const maxLevel = Math.min(100, max * 300);  // Peak detection
        
        // Use the highest of all measurements for best detection
        const finalLevel = Math.max(freqLevel, rmsLevel, maxLevel);
        
        // CAPTURE INTEGRITY: Always assume we have audio input if recording is active
        // The recording will continue regardless of detected levels
        setState(prev => ({
          ...prev,
          audioLevel: finalLevel,
          hasAudioInput: true, // ALWAYS true during recording - capture everything!
        }));
        
        // Update waveform data (keep last 50 samples)
        setWaveformData(prev => {
          const newData = [...prev, finalLevel];
          return newData.slice(-50);
        });
        
        animationFrameRef.current = requestAnimationFrame(monitorAudioLevel);
      };

      mediaRecorder.start(100); // Collect data every 100ms
      startTimeRef.current = Date.now();
      pausedTimeRef.current = 0;

      setState(prev => ({
        ...prev,
        isRecording: true,
        isPaused: false,
        recordingTime: 0,
        audioBlob: null,
        audioUrl: null,
        isEncrypting: false,
        encryptedRecording: null,
        error: null,
        audioLevel: 0,
        hasAudioInput: false,
        savedChunksCount: 0,
        lastChunkSavedAt: null,
        sessionId,
      }));

      // Start timer
      intervalRef.current = setInterval(updateRecordingTime, 1000);

      // Start audio level monitoring
      monitorAudioLevel();

      toast.success('Recording started - Speak clearly into your microphone');
    } catch (error) {
      console.error('Error starting recording:', error);
      toast.error('Failed to start recording. Please check microphone permissions.');
      // Clean up chunk storage if it was started
      if (sessionIdRef.current) {
        await chunkStorageService.endSession();
        sessionIdRef.current = null;
      }
    }
  }, [isSupported, updateRecordingTime, appointmentId, encounterId, patientId, providerId, patientName, startStoreSession, addSegment, completeStoreSession, clearActiveSession]);

  const stopRecording = useCallback(async () => {
    if (!mediaRecorderRef.current || !state.isRecording) return;

    // Clear timer first
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    // Clear chunk flush interval
    if (chunkFlushIntervalRef.current) {
      clearInterval(chunkFlushIntervalRef.current);
      chunkFlushIntervalRef.current = null;
    }

    // Stop audio level monitoring
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    // Clean up audio context
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
      analyserRef.current = null;
    }

    // Flush any remaining chunks and end chunk storage session
    try {
      await chunkStorageService.flushBuffer(true);
      await chunkStorageService.endSession();
    } catch (error) {
      console.error('[useAudioRecorder] Error ending chunk storage session:', error);
    }

    // End current segment in session store
    endCurrentSegment();

    // Stop the media recorder - the onstop handler defined in startRecording will process everything
    mediaRecorderRef.current.stop();
  }, [state.isRecording, endCurrentSegment]);

  const pauseRecording = useCallback(async () => {
    if (mediaRecorderRef.current && state.isRecording && !state.isPaused) {
      mediaRecorderRef.current.pause();

      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }

      // Flush chunks when pausing for safety
      try {
        await chunkStorageService.flushBuffer(true);
      } catch (error) {
        console.warn('[useAudioRecorder] Failed to flush on pause:', error);
      }

      // Update session store
      pauseStoreSession('user_paused');
      endCurrentSegment();

      setState(prev => ({ ...prev, isPaused: true }));
      toast.success('Recording paused');
    }
  }, [state.isRecording, state.isPaused, pauseStoreSession, endCurrentSegment]);

  const resumeRecording = useCallback(() => {
    if (mediaRecorderRef.current && state.isRecording && state.isPaused) {
      mediaRecorderRef.current.resume();

      const pauseTime = Date.now() - startTimeRef.current - state.recordingTime * 1000;
      pausedTimeRef.current += pauseTime;

      // Update session store
      resumeStoreSession();

      // Start new segment for this recording portion
      chunkStorageService.startNewSegment();
      addSegment({
        id: uuidv4(),
        startTime: Date.now(),
        duration: 0,
        chunkCount: 0,
        mimeType: mimeTypeRef.current,
      });

      setState(prev => ({ ...prev, isPaused: false }));

      // Restart timer
      intervalRef.current = setInterval(updateRecordingTime, 1000);

      toast.success('Recording resumed');
    }
  }, [state.isRecording, state.isPaused, state.recordingTime, updateRecordingTime, resumeStoreSession, addSegment]);

  const resetRecording = useCallback(async () => {
    if (mediaRecorderRef.current && state.isRecording) {
      mediaRecorderRef.current.stop();
    }

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    if (chunkFlushIntervalRef.current) {
      clearInterval(chunkFlushIntervalRef.current);
      chunkFlushIntervalRef.current = null;
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

    if (state.audioUrl) {
      URL.revokeObjectURL(state.audioUrl);
    }

    // Clean up chunk storage
    if (sessionIdRef.current) {
      try {
        await chunkStorageService.endSession();
        await chunkStorageService.deleteSessionChunks(sessionIdRef.current);
      } catch (error) {
        console.warn('[useAudioRecorder] Failed to cleanup on reset:', error);
      }
    }

    // Clear session store
    clearActiveSession();

    setState({
      isRecording: false,
      isPaused: false,
      recordingTime: 0,
      audioBlob: null,
      audioUrl: null,
      isEncrypting: false,
      encryptedRecording: null,
      error: null,
      audioLevel: 0,
      hasAudioInput: false,
      savedChunksCount: 0,
      lastChunkSavedAt: null,
      sessionId: null,
    });

    chunksRef.current = [];
    startTimeRef.current = 0;
    pausedTimeRef.current = 0;
    sessionIdRef.current = null;
  }, [state.isRecording, state.audioUrl, clearActiveSession]);

  return {
    ...state,
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    resetRecording,
    flushChunks,
    isSupported,
    waveformData,
  };
};